import React, { useEffect, useState, useCallback } from 'react';
import { FiDownload, FiPlus, FiEdit2, FiTrash2, FiArrowRight, FiCheckCircle, FiClock, FiAlertCircle, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import ProjectSearchSelect from '../components/ProjectSearchSelect';
import API from '../api/axios';
import './FinancePayment.css';

/* ── helpers ── */
const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '--';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';
const SERVER_ORIGIN = process.env.NODE_ENV === 'production'
  ? 'https://creativeglassworld.com'
  : 'http://localhost:9119';

function DonutChart({ received, pending, total }) {
  const r = 70, cx = 80, cy = 80, stroke = 22;
  const circ = 2 * Math.PI * r;
  const recDash = total > 0 ? (received / total) * circ : 0;
  const penDash = total > 0 ? (pending / total) * circ : 0;
  const pct = total > 0 ? Math.round((received / total) * 100) : 0;
  return (
    <svg viewBox="0 0 160 160" className="fp-donut">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
      {total > 0 && (
        <>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#ef4444" strokeWidth={stroke}
            strokeDasharray={`${penDash} ${circ - penDash}`}
            strokeDashoffset={-recDash}
            style={{ transform: 'rotate(-90deg)', transformOrigin: `${cx}px ${cy}px` }} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#22c55e" strokeWidth={stroke}
            strokeDasharray={`${recDash} ${circ - recDash}`}
            strokeDashoffset={0}
            style={{ transform: 'rotate(-90deg)', transformOrigin: `${cx}px ${cy}px` }} />
        </>
      )}
      <text x={cx} y={cy - 10} textAnchor="middle" className="fp-donut-val">{fmt(received)}</text>
      <text x={cx} y={cy + 10} textAnchor="middle" className="fp-donut-label">Received</text>
      <text x={cx} y={cy + 28} textAnchor="middle" className="fp-donut-pct">{pct}%</text>
    </svg>
  );
}

const PAYMENT_TYPES = ['Advance Payment', 'Running Payment', 'Final Payment'];
const MODES = ['Bank Transfer', 'NEFT', 'UPI', 'Cash', 'Cheque'];

const emptyForm = { paymentType: 'Running Payment', description: '', amount: '', discount: '', mode: 'Bank Transfer', paymentDate: new Date().toISOString().slice(0, 10), status: 'Received', referenceNumber: '', notes: '', receiptImage: null, existingReceiptUrl: '' };

export default function FinancePayment() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState('');
  const [zoomImageUrl, setZoomImageUrl] = useState('');

  useEffect(() => {
    if (!form.receiptImage) { setReceiptPreviewUrl(''); return; }
    const url = URL.createObjectURL(form.receiptImage);
    setReceiptPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [form.receiptImage]);

  useEffect(() => {
    API.get('/finance/projects').then(r => {
      setProjects(r.data);
      if (r.data.length > 0) setSelectedProject(r.data[0]);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const loadProject = useCallback((proj) => {
    if (!proj) return;
    setLoading(true);
    API.get(`/finance/${proj._id}`).then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { loadProject(selectedProject); }, [selectedProject, loadProject]);

  const openAdd = () => { setEditEntry(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (entry) => {
    setEditEntry(entry);
    setForm({
      paymentType: entry.paymentType || 'Running Payment',
      description: entry.description || '',
      amount: entry.amount || '',
      discount: entry.discount || '',
      mode: entry.mode || 'Bank Transfer',
      paymentDate: entry.paymentDate ? entry.paymentDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
      status: entry.status || 'Received',
      referenceNumber: entry.referenceNumber || '',
      notes: entry.notes || '',
      receiptImage: null,
      existingReceiptUrl: entry.receiptImage || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.amount || !form.paymentType) return toast.error('Amount and type are required');
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'receiptImage' || k === 'existingReceiptUrl') return;
        fd.append(k, v ?? '');
      });
      if (form.receiptImage) fd.append('receiptImage', form.receiptImage);

      if (editEntry) {
        await API.put(`/finance/entry/${editEntry._id}`, fd);
        toast.success('Payment updated');
      } else {
        await API.post(`/finance/${selectedProject._id}`, fd);
        toast.success('Payment added');
      }
      setShowModal(false);
      loadProject(selectedProject);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving payment');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkSettled = async () => {
    if (!selectedProject) return;
    if (!window.confirm('Mark payment collection as fully settled for this project? This applies even if the received amount doesn\'t match the project budget.')) return;
    try {
      await API.post(`/finance/${selectedProject._id}/settle`);
      toast.success('Payment marked as fully settled');
      loadProject(selectedProject);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating payment status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment entry?')) return;
    try {
      await API.delete(`/finance/entry/${id}`);
      toast.success('Payment deleted');
      loadProject(selectedProject);
    } catch {
      toast.error('Error deleting payment');
    }
  };

  const proj = data?.project;
  const budget = proj?.budget || 0;
  const received = data?.totalReceived || 0;
  const discount = data?.totalDiscount || 0;
  const pending = data?.pending || 0;
  const pct = budget > 0 ? Math.round((received / budget) * 100) : 0;

  const typeCards = [
    { label: 'Advance Payment', key: 'advance', color: '#22c55e', bg: '#f0fdf4', icon: '💵', desc: 'Initial advance received before project start.' },
    { label: 'Running Payment', key: 'running', color: '#3b82f6', bg: '#eff6ff', icon: '🔄', desc: 'Payments received during project execution.' },
    { label: 'Final Payment', key: 'final', color: '#f97316', bg: '#fff7ed', icon: '🏁', desc: 'Final payment after project completion.' },
  ];

  const statusOf = (key) => {
    const t = data?.byType?.[key];
    if (!t || t.amount === 0) return null;
    if (t.received >= t.amount) return 'Completed';
    if (t.received > 0) return 'In Progress';
    return 'Pending';
  };

  // Same customer may have multiple projects — surface all of them with their own pending amount
  // so switching the single-project dropdown doesn't hide dues sitting on their other projects.
  const clientId = proj?.client?._id || proj?.client;
  const sameClientProjects = clientId
    ? projects.filter(p => (p.client?._id || p.client) === clientId)
    : [];

  return (
    <Layout>
      {loading && <PageLoader text="Loading finance data..." />}
      <div className="fp-page">

        {/* ── Toolbar ── */}
        <div className="fp-toolbar">
          <ProjectSearchSelect
            projects={projects}
            value={selectedProject}
            onChange={setSelectedProject}
            objectMode={true}
            label="Select Project"
            placeholder="Choose a project…"
          />
          <div className="fp-toolbar-right">
            {selectedProject && (
              <div className="fp-selected-badge">
                <span className="fp-selected-id">{selectedProject.projectId}</span>
                <span className="fp-selected-title">{selectedProject.title}</span>
              </div>
            )}
            <button className="fp-btn-outline"><FiDownload size={14} /> Download Report</button>
          </div>
        </div>

        {sameClientProjects.length > 1 && (
          <div className="fp-card" style={{ padding: '1rem 1.25rem', marginBottom: '1rem' }}>
            <div style={{ fontWeight: 700, fontSize: '.85rem', marginBottom: '.75rem', color: '#0f172a' }}>
              {proj.client?.name || 'This customer'} has {sameClientProjects.length} projects — pending by project
            </div>
            <table className="fp-pending-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Budget</th>
                  <th>Received</th>
                  <th>Pending</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sameClientProjects.map(p => {
                  const b = p.budget || 0;
                  const r = p.received || 0;
                  const pend = Math.max(b - r, 0);
                  const isActive = p._id === selectedProject?._id;
                  return (
                    <tr key={p._id} style={isActive ? { background: '#eff6ff' } : undefined}>
                      <td>{p.projectId} - {p.title}</td>
                      <td>{fmt(b)}</td>
                      <td style={{ color: '#16a34a' }}>{fmt(r)}</td>
                      <td style={{ color: pend > 0 ? '#ef4444' : '#94a3b8', fontWeight: 600 }}>{fmt(pend)}</td>
                      <td>
                        {!isActive && (
                          <button className="fp-btn-outline" style={{ padding: '.25rem .6rem', fontSize: '.75rem' }} onClick={() => setSelectedProject(p)}>
                            View
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!data && !loading && (
          <div className="fp-empty">Select a project to view payment details.</div>
        )}
        {loading && <div className="fp-empty">Loading...</div>}

        {data && proj && (
          <>
            {/* ── Project Info + Payment Summary ── */}
            <div className="fp-top-row">
              {/* Project card */}
              <div className="fp-card fp-proj-card">
                <div className="fp-proj-img">
                  {proj.images?.[0] ? <img src={proj.images[0]} alt="" /> : <div className="fp-proj-img-ph" />}
                </div>
                <div className="fp-proj-info">
                  <div className="fp-proj-name-row">
                    <h2 className="fp-proj-name">{proj.projectId} - {proj.title}</h2>
                    <span className={`fp-status-badge fp-status-${(proj.status || '').replace('_', '-')}`}>
                      {proj.status?.replace('_', ' ') || 'N/A'}
                    </span>
                  </div>
                  <div className="fp-proj-meta-grid">
                    <div className="fp-meta-item">
                      <span className="fp-meta-label">Customer</span>
                      <span className="fp-meta-val">{proj.client?.name || '--'}</span>
                    </div>
                    <div className="fp-meta-item">
                      <span className="fp-meta-label">Project Value</span>
                      <span className="fp-meta-val">{fmt(budget)}</span>
                    </div>
                    <div className="fp-meta-item">
                      <span className="fp-meta-label">Start Date</span>
                      <span className="fp-meta-val">{fmtDate(proj.startDate)}</span>
                    </div>
                    <div className="fp-meta-item">
                      <span className="fp-meta-label">Expected Completion</span>
                      <span className="fp-meta-val">{fmtDate(proj.endDate)}</span>
                    </div>
                    <div className="fp-meta-item">
                      <span className="fp-meta-label">Supervisor</span>
                      <span className="fp-meta-val">{proj.designer?.name || '--'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Summary */}
              <div className="fp-card fp-summary-card">
                <h3 className="fp-summary-title">Payment Summary</h3>
                <div className="fp-summary-body">
                  <DonutChart received={received} pending={pending} total={budget} />
                  <div className="fp-summary-right">
                    <div className="fp-summary-legend">
                      <div className="fp-legend-row">
                        <span className="fp-legend-dot" style={{ background: '#1e40af' }} />
                        <span>Project Value</span>
                        <span className="fp-legend-amt">{fmt(budget)}</span>
                      </div>
                      <div className="fp-legend-row">
                        <span className="fp-legend-dot" style={{ background: '#22c55e' }} />
                        <span>Amount Received</span>
                        <span className="fp-legend-amt" style={{ color: '#22c55e' }}>{fmt(received)}</span>
                      </div>
                      <div className="fp-legend-row">
                        <span className="fp-legend-dot" style={{ background: '#ef4444' }} />
                        <span>Pending Amount</span>
                        <span className="fp-legend-amt" style={{ color: '#ef4444' }}>{fmt(pending)}</span>
                      </div>
                    </div>
                    <div className="fp-status-row">
                      <span className="fp-status-lbl">Payment Status</span>
                      <span className={`fp-track-badge ${pct >= 100 ? 'fp-track-done' : pct >= 50 ? 'fp-track-on' : 'fp-track-behind'}`}>
                        {pct >= 100 ? 'Completed' : pct >= 50 ? 'On Track' : 'Behind'}
                      </span>
                    </div>
                    <p className="fp-pct-line">{pct}% of project value received</p>
                    {(data.project?.stages || []).find(s => s.key === 'payment_collection')?.status !== 'Completed' && (
                      <button className="fp-btn-outline fp-settle-btn" onClick={handleMarkSettled}>
                        <FiCheckCircle size={13} /> Mark as Fully Settled
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Payment Types ── */}
            <div className="fp-card">
              <h3 className="fp-section-title">Payment Types</h3>
              <div className="fp-type-grid">
                {typeCards.map(tc => {
                  const t = data.byType?.[tc.key] || { amount: 0, received: 0 };
                  const st = statusOf(tc.key);
                  return (
                    <div key={tc.key} className="fp-type-card" style={{ borderTop: `3px solid ${tc.color}` }}>
                      <div className="fp-type-header">
                        <span className="fp-type-icon" style={{ background: tc.bg }}>{tc.icon}</span>
                        <span className="fp-type-label" style={{ color: tc.color }}>{tc.label}</span>
                      </div>
                      <p className="fp-type-desc">{tc.desc}</p>
                      <div className="fp-type-amounts">
                        <div><p className="fp-type-amt-lbl">Amount</p><p className="fp-type-amt">{fmt(t.amount)}</p></div>
                        <div><p className="fp-type-amt-lbl">Received</p><p className="fp-type-amt" style={{ color: '#22c55e' }}>{fmt(t.received)}</p></div>
                      </div>
                      {st && (
                        <div className={`fp-type-status fp-ts-${st.toLowerCase().replace(' ', '-')}`}>
                          {st === 'Completed' && <FiCheckCircle />}
                          {st === 'In Progress' && <FiClock />}
                          {st === 'Pending' && <FiAlertCircle />}
                          {st}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Payment History + Right col ── */}
            <div className="fp-mid-row">
              {/* Payment History */}
              <div className="fp-card fp-history-card">
                <div className="fp-card-header">
                  <h3 className="fp-section-title" style={{ margin: 0 }}>Payment History</h3>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button className="fp-view-all">View All</button>
                    <button className="fp-btn-primary" onClick={openAdd}><FiPlus /> Add Payment</button>
                  </div>
                </div>
                <div className="fp-table-wrap">
                  <table className="fp-table">
                    <thead>
                      <tr>
                        <th>Date</th><th>Payment Type</th><th>Description</th>
                        <th>Amount (₹)</th><th>Received By</th><th>Mode</th><th>Status</th><th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.payments || []).length === 0 ? (
                        <tr><td colSpan={8} className="fp-empty-row">No payment entries yet.</td></tr>
                      ) : (data.payments || []).map(p => (
                        <tr key={p._id}>
                          <td>{fmtDate(p.paymentDate)}</td>
                          <td>{p.paymentType || '--'}</td>
                          <td>{p.description || p.notes || '--'}</td>
                          <td className="fp-amt-cell">{Number(p.amount || 0).toLocaleString('en-IN')}</td>
                          <td>{p.createdBy?.name || '--'}</td>
                          <td>{p.mode || '--'}</td>
                          <td>
                            <span className={`fp-badge fp-badge-${(p.status || 'pending').toLowerCase()}`}>
                              {p.status || 'Pending'}
                            </span>
                          </td>
                          <td>
                            <div className="fp-actions">
                              <button className="fp-icon-btn" onClick={() => openEdit(p)}><FiEdit2 /></button>
                              <button className="fp-icon-btn fp-icon-del" onClick={() => handleDelete(p._id)}><FiTrash2 /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {(data.payments || []).length > 0 && (
                      <tfoot>
                        <tr>
                          <td colSpan={3} className="fp-tfoot-label">Total Received</td>
                          <td className="fp-tfoot-amt" colSpan={5}>{fmt(received)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              {/* Right column */}
              <div className="fp-right-col">
                {/* Payment Overview progress bars */}
                <div className="fp-card">
                  <h3 className="fp-section-title">Payment Overview</h3>
                  <div className="fp-progress-list">
                    <div className="fp-progress-item">
                      <div className="fp-progress-row"><span>Project Value</span><span>{fmt(budget)}</span></div>
                      <div className="fp-progress-bar"><div className="fp-progress-fill fp-pf-blue" style={{ width: '100%' }} /></div>
                    </div>
                    <div className="fp-progress-item">
                      <div className="fp-progress-row">
                        <span>Amount Received</span>
                        <span style={{ color: '#22c55e' }}>{fmt(received)} ({pct}%)</span>
                      </div>
                      <div className="fp-progress-bar"><div className="fp-progress-fill fp-pf-green" style={{ width: `${pct}%` }} /></div>
                    </div>
                    {discount > 0 && (
                      <div className="fp-progress-item">
                        <div className="fp-progress-row">
                          <span>Discount Given</span>
                          <span style={{ color: '#f97316' }}>{fmt(discount)}</span>
                        </div>
                      </div>
                    )}
                    <div className="fp-progress-item">
                      <div className="fp-progress-row">
                        <span>Pending Amount</span>
                        <span style={{ color: '#ef4444' }}>{fmt(pending)} ({100 - pct}%)</span>
                      </div>
                      <div className="fp-progress-bar"><div className="fp-progress-fill fp-pf-red" style={{ width: `${100 - pct}%` }} /></div>
                    </div>
                  </div>
                </div>

                {/* Pending Payments */}
                <div className="fp-card">
                  <h3 className="fp-section-title">Pending Payments</h3>
                  {(data.pendingPayments || []).length === 0 ? (
                    <p className="fp-empty-sm">No pending payments.</p>
                  ) : (
                    <>
                      <div className="fp-table-wrap fp-table-wrap-plain">
                      <table className="fp-pending-table">
                        <thead><tr><th>Payment Type</th><th>Description</th><th>Pending (₹)</th><th>Expected Date</th><th>Action</th></tr></thead>
                        <tbody>
                          {(data.pendingPayments || []).map(p => (
                            <tr key={p._id}>
                              <td>{p.paymentType}</td>
                              <td>{p.description || p.notes || '--'}</td>
                              <td className="fp-amt-red">{fmt(p.amount)}</td>
                              <td>{fmtDate(p.paymentDate)}</td>
                              <td><button className="fp-followup-btn">Follow Up</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                      <div className="fp-pending-alert">
                        <FiAlertCircle />
                        {data.pendingPayments.length} payment{data.pendingPayments.length > 1 ? 's are' : ' is'} pending. Follow up with the customer to collect the due amount.
                      </div>
                    </>
                  )}
                </div>

                {/* Recent Payment Activity */}
                <div className="fp-card">
                  <div className="fp-card-header">
                    <h3 className="fp-section-title" style={{ margin: 0 }}>Recent Payment Activity</h3>
                  </div>
                  <div className="fp-activity-list">
                    {(data.recentActivity || []).length === 0 ? (
                      <p className="fp-empty-sm">No recent activity.</p>
                    ) : (data.recentActivity || []).map((p, i) => (
                      <div key={p._id} className="fp-activity-row">
                        <div className={`fp-activity-dot ${i === 0 ? 'fp-dot-green' : 'fp-dot-blue'}`} />
                        <div>
                          <p className="fp-activity-date">{fmtDate(p.paymentDate)} <span className="fp-activity-time">{fmtTime(p.paymentDate)}</span></p>
                          <p className="fp-activity-desc">Received {fmt(p.amount)} for {p.description || p.paymentType}</p>
                          <p className="fp-activity-meta">Mode: {p.mode}</p>
                          <p className="fp-activity-meta">Received By: {p.createdBy?.name || '--'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button className="fp-view-all-link">View All Activities <FiArrowRight /></button>
                </div>
              </div>
            </div>

          </>
        )}

        {/* ── Add / Edit Modal ── */}
        {showModal && (
          <div className="fp-modal-overlay">
            <div className="fp-modal" onClick={e => e.stopPropagation()}>
              <h3 className="fp-modal-title">{editEntry ? 'Edit Payment' : 'Add Payment'}</h3>
              <div className="fp-modal-grid">
                <div className="fp-field">
                  <label>Payment Type *</label>
                  <ProjectSearchSelect
                    projects={PAYMENT_TYPES.map(t => ({ _id: t, title: t }))}
                    value={form.paymentType}
                    objectMode={false}
                    onChange={id => setForm(f => ({ ...f, paymentType: id || PAYMENT_TYPES[0] }))}
                    placeholder="Select Payment Type…"
                  />
                </div>
                <div className="fp-field">
                  <label>Amount (₹) *</label>
                  <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" />
                </div>
                <div className="fp-field">
                  <label>Discount (₹)</label>
                  <input type="number" value={form.discount} onChange={e => setForm(f => ({ ...f, discount: e.target.value }))} placeholder="0" />
                </div>
                <div className="fp-field">
                  <label>Description</label>
                  <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. Material Purchase" />
                </div>
                <div className="fp-field">
                  <label>Mode</label>
                  <ProjectSearchSelect
                    projects={MODES.map(m => ({ _id: m, title: m }))}
                    value={form.mode}
                    objectMode={false}
                    onChange={id => setForm(f => ({ ...f, mode: id || MODES[0] }))}
                    placeholder="Select Mode…"
                  />
                </div>
                <div className="fp-field">
                  <label>Payment Date</label>
                  <input type="date" value={form.paymentDate} onChange={e => setForm(f => ({ ...f, paymentDate: e.target.value }))} />
                </div>
                <div className="fp-field">
                  <label>Status</label>
                  <ProjectSearchSelect
                    projects={[{ _id: 'Received', title: 'Received' }, { _id: 'Pending', title: 'Pending' }]}
                    value={form.status}
                    objectMode={false}
                    onChange={id => setForm(f => ({ ...f, status: id || 'Received' }))}
                    placeholder="Select Status…"
                  />
                </div>
                <div className="fp-field fp-field-full">
                  <label>Reference Number</label>
                  <input type="text" value={form.referenceNumber} onChange={e => setForm(f => ({ ...f, referenceNumber: e.target.value }))} placeholder="UTR / Cheque No." />
                </div>
                <div className="fp-field fp-field-full">
                  <label>Notes</label>
                  <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Additional notes..." />
                </div>
                <div className="fp-field fp-field-full">
                  <label>Receipt / Proof Image</label>
                  {form.existingReceiptUrl && !form.receiptImage && (() => {
                    const url = form.existingReceiptUrl.startsWith('http') ? form.existingReceiptUrl : `${SERVER_ORIGIN}${form.existingReceiptUrl}`;
                    console.log('[FinancePayment] receipt image URL:', url);
                    
                    return (
                      <div className="fp-receipt-preview">
                        <img
                          src={url}
                          alt="Receipt"
                          onClick={() => setZoomImageUrl(url)}
                          onError={(e) => console.error('[FinancePayment] receipt image failed to load:', url, e)}
                          onLoad={() => console.log('[FinancePayment] receipt image loaded OK:', url)}
                        />
                        <span>Current receipt attached</span>
                      </div>
                    );
                  })()}
                  {form.receiptImage && receiptPreviewUrl && (
                    <div className="fp-receipt-preview">
                      <img src={receiptPreviewUrl} alt="New receipt preview" onClick={() => setZoomImageUrl(receiptPreviewUrl)} />
                      <span>{form.receiptImage.name}</span>
                      <button type="button" className="fp-receipt-remove" onClick={() => setForm(f => ({ ...f, receiptImage: null }))}>
                        <FiTrash2 size={12} />
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => setForm(f => ({ ...f, receiptImage: e.target.files[0] || null }))}
                  />
                </div>
              </div>
              <div className="fp-modal-footer">
                <button className="fp-btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="fp-btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Payment'}</button>
              </div>
            </div>
          </div>
        )}

        {zoomImageUrl && (
          <div className="fp-zoom-overlay" onClick={() => setZoomImageUrl('')}>
            <button className="fp-zoom-close" onClick={() => setZoomImageUrl('')}><FiX size={20} /></button>
            <img src={zoomImageUrl} alt="Receipt full view" onClick={e => e.stopPropagation()} />
          </div>
        )}
      </div>
    </Layout>
  );
}
